/**
 * Hooks del canal PQRSF (Res. DIAN 227/2025 art. 1.5.1.8.1.1 num. 10).
 *
 * - usePqrsfList(filter)  — bandeja paginada con filtros estado/tipo.
 * - usePqrsfStats()       — conteos del encabezado.
 * - usePqrsfDetail(id)    — ticket + línea de tiempo de trazabilidad.
 * - usePqrsfRespond()     — responder de fondo (resuelto | cerrado).
 * - usePqrsfChangeStatus()— transición de estado.
 * - usePqrsfAddNote()     — nota en la trazabilidad (interna o pública).
 *
 * Toda mutación invalida el detalle Y la bandeja: la trazabilidad es el
 * producto del canal, así que la línea de tiempo nunca puede quedar
 * mostrando un estado viejo.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import {
  pqrsfApi,
  type PqrsfDetail,
  type PqrsfEvent,
  type PqrsfFilter,
  type PqrsfListResponse,
  type PqrsfStats,
  type PqrsfStatus,
  type PqrsfTicket,
} from '@/lib/api'

const KEY = ['admin', 'pqrsf'] as const

export function usePqrsfList(filter: PqrsfFilter) {
  return useQuery<PqrsfListResponse>({
    queryKey: [...KEY, 'list', filter],
    queryFn: () => pqrsfApi.list(filter),
    staleTime: 30 * 1000,
    placeholderData: (prev) => prev,
  })
}

export function usePqrsfStats() {
  return useQuery<PqrsfStats>({
    queryKey: [...KEY, 'stats'],
    queryFn: () => pqrsfApi.stats(),
    staleTime: 60 * 1000,
  })
}

export function usePqrsfDetail(id: string | undefined) {
  return useQuery<PqrsfDetail>({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => pqrsfApi.get(id as string),
    enabled: Boolean(id),
  })
}

/** Mensaje de error legible desde la respuesta del backend. */
function apiMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { message?: string } } }
  return e?.response?.data?.message || fallback
}

export function usePqrsfRespond(id: string) {
  const qc = useQueryClient()
  return useMutation<
    PqrsfTicket,
    Error,
    { response_text: string; status?: PqrsfStatus }
  >({
    mutationFn: (body) => pqrsfApi.respond(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      toast.success('Respuesta registrada en el trámite')
    },
    onError: (err) => {
      toast.error(apiMessage(err, 'No se pudo registrar la respuesta'))
    },
  })
}

export function usePqrsfChangeStatus(id: string) {
  const qc = useQueryClient()
  return useMutation<PqrsfTicket, Error, { status: PqrsfStatus; comment?: string }>({
    mutationFn: (body) => pqrsfApi.changeStatus(id, body),
    onSuccess: (ticket) => {
      qc.invalidateQueries({ queryKey: KEY })
      toast.success(`Trámite movido a "${ticket.status}"`)
    },
    onError: (err) => {
      toast.error(apiMessage(err, 'No se pudo cambiar el estado'))
    },
  })
}

export function usePqrsfAddNote(id: string) {
  const qc = useQueryClient()
  return useMutation<PqrsfEvent, Error, { comment: string; is_public: boolean }>({
    mutationFn: (body) => pqrsfApi.addNote(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      toast.success('Nota agregada a la trazabilidad')
    },
    onError: (err) => {
      toast.error(apiMessage(err, 'No se pudo agregar la nota'))
    },
  })
}
